// Analyses filters (DESIGN_SPEC §11.3), kept in the URL so a filtered list is a link:
//   ?q=        search: sample name, SHA-256, job id, label, tag (title, rule id, technique); every term must match
//   ?state=    live (on a node: live or overdue, lib/records liveness) | failed,cancelled (chips), or any of
//              queued running completed failed cancelled
//   ?verdict=  malicious | suspicious | benign (completed runs)
//   ?profile=  ?network= isolated|simulated|internet   ?kind= file|url   ?limit= rows to load (50, 100, …)
//   readFilters(params) · toParams(filters) · pressedChip · withChip · isFiltered · cleared
//   matchesQuery(eff, q) · matches(eff, filters, { chip?, now? }) · chipCounts(effs, filters, now?) · describe(filters)
import { livenessOf, type Eff } from "./listState";

export const PAGE = 50;

export type ChipKey = "all" | "live" | "malicious" | "suspicious" | "benign" | "stopped";

export type Filters = {
  q: string;
  state: readonly string[];
  verdict: readonly string[];
  profile: string;
  network: string;
  kind: string;
  limit: number;
};

export const CHIPS: readonly { key: ChipKey; label: string; state: readonly string[]; verdict: readonly string[] }[] = [
  { key: "all", label: "All", state: [], verdict: [] },
  { key: "live", label: "Live", state: ["live"], verdict: [] },
  { key: "malicious", label: "Malicious", state: [], verdict: ["malicious"] },
  { key: "suspicious", label: "Suspicious", state: [], verdict: ["suspicious"] },
  { key: "benign", label: "Benign", state: [], verdict: ["benign"] },
  { key: "stopped", label: "Failed or cancelled", state: ["failed", "cancelled"], verdict: [] },
];

const STATES = new Set(["live", "queued", "running", "completed", "failed", "cancelled"]);
const VERDICTS = new Set(["malicious", "suspicious", "benign"]);
const NETWORKS = new Set(["isolated", "simulated", "internet"]);
const KINDS = new Set(["file", "url"]);

const list = (v: string | null, ok: ReadonlySet<string>): string[] =>
  [...new Set((v ?? "").split(",").map((s) => s.trim().toLowerCase()))].filter((s) => ok.has(s)).sort();

export function readFilters(p: URLSearchParams): Filters {
  const limit = Number.parseInt(p.get("limit") ?? "", 10);
  const network = (p.get("network") ?? "").toLowerCase();
  const kind = (p.get("kind") ?? "").toLowerCase();
  return {
    q: p.get("q") ?? "",
    state: list(p.get("state"), STATES),
    verdict: list(p.get("verdict"), VERDICTS),
    profile: (p.get("profile") ?? "").trim(),
    network: NETWORKS.has(network) ? network : "",
    kind: KINDS.has(kind) ? kind : "",
    limit: Number.isFinite(limit) && limit > PAGE ? Math.min(5000, Math.ceil(limit / PAGE) * PAGE) : PAGE,
  };
}

/** The filters as URL params (empty values and the default limit are left out; other params kept). */
export function toParams(f: Filters, base?: URLSearchParams): URLSearchParams {
  const p = new URLSearchParams(base);
  const set = (k: string, v: string) => (v ? p.set(k, v) : p.delete(k));
  set("q", f.q.trim() ? f.q : "");
  set("state", [...f.state].sort().join(","));
  set("verdict", [...f.verdict].sort().join(","));
  set("profile", f.profile);
  set("network", f.network);
  set("kind", f.kind);
  set("limit", f.limit > PAGE ? String(f.limit) : "");
  return p;
}

const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);

/** The chip that shows these filters, or null for a combination no chip shows (a hand-made URL). */
export function pressedChip(f: Filters): ChipKey | null {
  return CHIPS.find((c) => same(c.state, f.state) && same(c.verdict, f.verdict))?.key ?? null;
}

/** Choosing a chip replaces the state/verdict filter; choosing the pressed one again shows all. */
export function withChip(f: Filters, key: ChipKey): Filters {
  const target = pressedChip(f) === key ? CHIPS[0] : CHIPS.find((c) => c.key === key) ?? CHIPS[0];
  return { ...f, state: target.state, verdict: target.verdict };
}

export function isFiltered(f: Filters): boolean {
  return !!(f.q.trim() || f.state.length || f.verdict.length || f.profile || f.network || f.kind);
}

export function cleared(f: Filters): Filters {
  return { ...f, q: "", state: [], verdict: [], profile: "", network: "", kind: "" };
}

function matchesQuery(e: Eff, q: string): boolean {
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const j = e.job;
  const hay: string[] = [j.filename ?? "", j.url ?? "", j.sample_sha ?? "", j.id, ...(j.labels ?? []), e.verdict?.label ?? ""];
  for (const t of e.verdict?.tags ?? []) hay.push(t.title, t.id, ...(t.mitre_ids ?? []));
  const lower = hay.filter(Boolean).map((s) => s.toLowerCase());
  return terms.every((t) => lower.some((s) => s.includes(t)));
}

/** The "Live" chip and the status bar's "N live · M overdue" link: runs on a node, within their timeout or past it. */
const onNode = (e: Eff, now: number) => {
  const l = livenessOf(e, now);
  return l === "live" || l === "overdue";
};

function stateMatches(e: Eff, s: string, now: number): boolean {
  return s === "live" ? onNode(e, now) : e.bucket === s;
}

export function matches(e: Eff, f: Filters, opts: { chip?: boolean; now?: number } = {}): boolean {
  const j = e.job;
  if (f.profile && j.profile !== f.profile) return false;
  if (f.network && j.network !== f.network) return false;
  if (f.kind && j.kind !== f.kind) return false;
  if (opts.chip !== false) {
    const now = opts.now ?? Date.now();
    if (f.state.length && !f.state.some((s) => stateMatches(e, s, now))) return false;
    if (f.verdict.length && !(e.bucket === "completed" && !!e.verdict && f.verdict.includes(e.verdict.label))) return false;
  }
  return matchesQuery(e, f.q);
}

/** Per-chip counts under the search and selects (not under the chip itself). */
export function chipCounts(effs: readonly Eff[], f: Filters, now = Date.now()): Record<ChipKey, number> {
  const out: Record<ChipKey, number> = { all: 0, live: 0, malicious: 0, suspicious: 0, benign: 0, stopped: 0 };
  for (const e of effs) {
    if (!matches(e, f, { chip: false })) continue;
    out.all++;
    if (onNode(e, now)) out.live++;
    else if (e.bucket === "completed" && e.verdict) out[e.verdict.label]++;
    else if (e.bucket === "failed" || e.bucket === "cancelled") out.stopped++;
  }
  return out;
}

/** The active filters as words, for "No runs match …". `quoted` parts are the search text. */
export function describe(f: Filters): { text: string; quoted?: boolean }[] {
  const out: { text: string; quoted?: boolean }[] = [];
  const chip = pressedChip(f);
  if (chip && chip !== "all") out.push({ text: CHIPS.find((c) => c.key === chip)?.label.toLowerCase() ?? chip });
  else if (!chip) out.push({ text: [...f.state, ...f.verdict].join(" or ") });
  if (f.profile) out.push({ text: f.profile });
  if (f.network) out.push({ text: f.network });
  if (f.kind) out.push({ text: f.kind === "url" ? "URLs" : "files" });
  if (f.q.trim()) out.push({ text: f.q.trim(), quoted: true });
  return out;
}
